import { useCurrentTenant } from '@/shared/http';

import { useAuth } from '../auth';

// Base dos favoritos e dos recentes da Início (trava Lite, LGPD): chave por correspondente e usuário e
// recusa fixa de dado pessoal. Quem grava no navegador é `favorites.ts` e `recents.ts`, na lista de
// permissão do `eslint.config.js`.

/** Sem correspondente escolhido (usuário de corban): o próprio, fixo para o `sub`. */
export const OWN_TENANT = '_';

const SEGMENT = /[^A-Za-z0-9_-]/g;

/** `<prefixo><tenant>.<sub>`; sem usuário, nada é guardado. */
export function personalKey(prefix: string, tenant: string | undefined, user: string | undefined): string | undefined {
  const sub = user?.replace(SEGMENT, '');
  if (!sub) return undefined;
  const scope = tenant?.replace(SEGMENT, '') || OWN_TENANT;
  return `${prefix}${scope}.${sub}`;
}

/** O escopo pessoal de quem está logado: correspondente atual e `sub` (nunca nome nem e-mail). */
export function usePersonalScope(): { readonly tenant: string | undefined; readonly user: string | undefined } {
  const auth = useAuth();
  const tenant = useCurrentTenant();
  return { tenant, user: auth.isAuthenticated ? auth.user?.sub : undefined };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CNPJ = /^[A-Z0-9]{2}\.?[A-Z0-9]{3}\.?[A-Z0-9]{3}\/?[A-Z0-9]{4}-?\d{2}$/i;

/**
 * Recusa fixa: e-mail, CPF, CNPJ (inclusive alfanumérico) e telefone, formatados ou crus. Id interno
 * (UUID) e número curto (operação) passam.
 */
export function isSensitiveValue(value: string): boolean {
  let text: string;
  try {
    text = decodeURIComponent(value).trim();
  } catch {
    return true;
  }
  if (text === '' || UUID.test(text)) return false;
  if (text.includes('@')) return true;
  if (CNPJ.test(text)) return true;
  const digits = text.replace(/[\s().+/-]/g, '');
  return /^\d{10,}$/.test(digits);
}

/** Algum trecho do caminho é dado pessoal: o caminho inteiro fica de fora. */
export function hasSensitiveSegment(path: string): boolean {
  return path.split('/').some((segment) => segment !== '' && isSensitiveValue(segment));
}

/** Leitura e escrita tolerantes: aba privada, cota ou armazenamento bloqueado não quebram a tela. */
export function readJson(storage: () => Storage | undefined, key: string): unknown {
  try {
    const raw = storage()?.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : undefined;
  } catch {
    return undefined;
  }
}

export function writeJson(storage: () => Storage | undefined, key: string, value: unknown): void {
  try {
    storage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Preferência por pessoa, não essencial: sem armazenamento, vale só a memória.
  }
}

/** Apaga todas as chaves com o prefixo (logout). */
export function removeByPrefix(storage: Storage | undefined, prefix: string): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Sem armazenamento: não há o que limpar.
  }
}
